import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart, calculateDistance } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { getUserLocations } from './userLocationsApi';
import { fetchBranchById } from './branchesApi';
import {
  fetchRentalProducts, createRentalOrder
} from './rentalApi';
import {
  Box, Typography, Container, Grid, Button, Chip, Stack, Avatar,
  TextField, Paper, Divider, Dialog, DialogContent, DialogActions,
  Alert, Snackbar, CircularProgress, Skeleton, IconButton, Tooltip,
  alpha, useTheme, useMediaQuery, ToggleButton, ToggleButtonGroup,
  Collapse, FormControl, InputLabel, Select, MenuItem, Portal
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SecurityIcon from '@mui/icons-material/Security';
import InventoryIcon from '@mui/icons-material/Inventory';
import VerifiedIcon from '@mui/icons-material/Verified';
import DescriptionIcon from '@mui/icons-material/Description';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PaletteIcon from '@mui/icons-material/Palette';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import AddLocationIcon from '@mui/icons-material/AddLocation';

// ═══════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// ═══════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════

const PRICE_TYPE_CONFIG = {
  hourly: { labelAr: 'ساعة', labelEn: 'Hour', pluralAr: 'ساعات', pluralEn: 'Hours' },
  daily: { labelAr: 'يوم', labelEn: 'Day', pluralAr: 'أيام', pluralEn: 'Days' },
  weekly: { labelAr: 'أسبوع', labelEn: 'Week', pluralAr: 'أسابيع', pluralEn: 'Weeks' },
  monthly: { labelAr: 'شهر', labelEn: 'Month', pluralAr: 'أشهر', pluralEn: 'Months' },
};

// ═══════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════

const getImageUrl = (imageData) => {
  if (!imageData) return null;
  if (typeof imageData === 'string') {
    try {
      const p = JSON.parse(imageData);
      return p.large || p.medium || p.thumbnail || imageData;
    } catch {
      return imageData;
    }
  }
  if (typeof imageData === 'object') return imageData.large || imageData.medium || imageData.thumbnail;
  return null;
};

const getLocationIcon = (name) => {
  if (!name) return LocationOnIcon;
  const lower = name.toLowerCase();
  if (lower.includes('home') || lower.includes('منزل') || lower.includes('بيت')) return HomeIcon;
  if (lower.includes('office') || lower.includes('عمل') || lower.includes('مكتب')) return BusinessIcon;
  return LocationOnIcon;
};

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════

export default function RentalDetailPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { productId } = useParams();
  const navigate = useNavigate();
  const { selectedBranch, user } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const isAr = i18n.language === 'ar';
  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const branchId = selectedBranch?.id || selectedBranch?.Id;

  // ═══ STATE ═══
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submitting, setSubmitting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);

  // Location & delivery
  const [userLocations, setUserLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [branchData, setBranchData] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [distance, setDistance] = useState(0);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // ═══ BOOKING FORM ═══
  const [bookingForm, setBookingForm] = useState({
    startDate: '',
    endDate: '',
    duration: 1,
    selectedColor: '',
    customerNotes: '',
    isDelivery: false,
  });

  // ═══ INPUT STYLE ═══
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, bgcolor: 'background.paper',
      fontSize: { xs: '0.9rem', sm: '0.95rem' },
    },
    '& .MuiInputLabel-root': { fontSize: { xs: '0.85rem', sm: '0.9rem' } },
  };

  // ═══ SELECTED LOCATION ═══
  const selectedLocation = useMemo(() => {
    return userLocations.find(l => l.Id === selectedLocationId) || null;
  }, [userLocations, selectedLocationId]);

  // ═══ LOAD PRODUCT ═══
  useEffect(() => {
    const load = async () => {
      if (!branchId || !productId) return;
      setLoading(true);
      try {
        const products = await fetchRentalProducts(branchId, selectedCurrency);
        const found = products?.find(p => String(p.Id) === String(productId));
        if (found) {
          setProduct(found);
          let colors = found.ProductColors;
          if (typeof colors === 'string') {
            try { colors = JSON.parse(colors); } catch { colors = null; }
          }
          if (colors && colors.length > 0) {
            setBookingForm(prev => ({ ...prev, selectedColor: colors[0].colorName }));
          }
        }
      } catch (err) {
        console.error('Error loading product:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [branchId, productId, selectedCurrency]);

  // ═══ LOAD BRANCH DATA ═══
  useEffect(() => {
    const loadBranch = async () => {
      if (selectedBranch?.id) {
        try {
          const fresh = await fetchBranchById(selectedBranch.id);
          setBranchData(fresh);
        } catch (e) {
          setBranchData(selectedBranch);
        }
      } else {
        setBranchData(selectedBranch);
      }
    };
    loadBranch();
  }, [selectedBranch?.id]);

  // ═══ LOAD USER LOCATIONS ═══
  useEffect(() => {
    const loadLocations = async () => {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const u = JSON.parse(userStr);
      const userId = u.Id || u.id;
      if (!userId) return;
      setLoadingLocations(true);
      try {
        const locations = await getUserLocations(userId);
        setUserLocations(locations || []);
        const defaultLoc = locations?.find(l => l.IsDefault) || locations?.[0];
        if (defaultLoc) setSelectedLocationId(defaultLoc.Id);
      } catch (e) {
        console.error('Error loading locations:', e);
      } finally {
        setLoadingLocations(false);
      }
    };
    loadLocations();
  }, [user]);

  // ═══ CALCULATE DELIVERY FEE ═══
  useEffect(() => {
    if (!bookingForm.isDelivery || !branchData || !selectedLocation) {
      if (!bookingForm.isDelivery) { setDeliveryFee(0); setDistance(0); }
      return;
    }
    const branchLat = branchData.latitude || branchData.Latitude;
    const branchLng = branchData.longitude || branchData.Longitude;
    if (!branchLat || !branchLng || !selectedLocation.Latitude || !selectedLocation.Longitude) {
      const baseFee = Number(branchData.deliveryFeeValue || branchData.deliveryfeevalue || branchData.DeliveryFeeValue || 0);
      setDeliveryFee(baseFee);
      setDistance(0);
      return;
    }
    const dist = calculateDistance(selectedLocation.Latitude, selectedLocation.Longitude, branchLat, branchLng);
    setDistance(dist);
    const feeType = branchData.deliveryFeeType || branchData.deliveryfeetype || branchData.DeliveryFeeType || 'fixedAmount';
    const feeValue = Number(branchData.deliveryFeeValue || branchData.deliveryfeevalue || branchData.DeliveryFeeValue || 0);
    const rangeKm = Number(branchData.DeliveryRangeKm || branchData.deliveryRangeKm || branchData.deliveryrangekm || 10);
    let fee = 0;
    if (feeType === 'byKm') {
      fee = dist <= rangeKm ? dist * feeValue : (rangeKm * feeValue) + 500;
    } else {
      fee = dist <= rangeKm ? feeValue : feeValue + 500;
    }
    setDeliveryFee(Math.max(0, Math.round(fee)));
  }, [bookingForm.isDelivery, branchData, selectedLocation]);

  // ═══ DERIVED VALUES ═══
  const priceConfig = product ? (PRICE_TYPE_CONFIG[product.RentalPriceType] || PRICE_TYPE_CONFIG.daily) : {};
  const rentalPrice = product?.RentalCurrencyPrices?.[selectedCurrency] || product?.RentalCurrencyPrices?.YER || 0;
  const depositPrice = product?.RentalDepositCurrencyPrices?.[selectedCurrency] || product?.RentalDepositCurrencyPrices?.YER || 0;
  const totalRentalPrice = rentalPrice * bookingForm.duration;
  const grandTotal = totalRentalPrice + depositPrice + (bookingForm.isDelivery ? deliveryFee : 0);
  const isAvailable = product && product.IsActive !== false && (product.RentalStock === null || product.RentalStock > 0);

  let productColors = product?.ProductColors;
  if (typeof productColors === 'string') {
    try { productColors = JSON.parse(productColors); } catch { productColors = null; }
  }

  const productImages = useMemo(() => {
    if (!product) return [];
    const imgs = [];
    const mainImg = getImageUrl(product.ImageUrl) || getImageUrl(product.ImageJson);
    if (mainImg) imgs.push(mainImg);
    let additionalImgs = product.ProductImages;
    if (typeof additionalImgs === 'string') {
      try { additionalImgs = JSON.parse(additionalImgs); } catch { additionalImgs = null; }
    }
    if (Array.isArray(additionalImgs)) {
      additionalImgs.forEach(img => { const url = getImageUrl(img); if (url) imgs.push(url); });
    }
    return imgs;
  }, [product]);

  // ═══ AUTO END DATE ═══
  useEffect(() => {
    if (bookingForm.startDate && bookingForm.duration && product) {
      const start = new Date(bookingForm.startDate);
      let end = new Date(start);
      const dur = parseInt(bookingForm.duration) || 1;
      switch (product.RentalPriceType) {
        case 'hourly': end.setHours(end.getHours() + dur); break;
        case 'daily': end.setDate(end.getDate() + dur); break;
        case 'weekly': end.setDate(end.getDate() + dur * 7); break;
        case 'monthly': end.setMonth(end.getMonth() + dur); break;
        default: end.setDate(end.getDate() + dur);
      }
      setBookingForm(prev => ({ ...prev, endDate: end.toISOString().split('T')[0] }));
    }
  }, [bookingForm.startDate, bookingForm.duration, product]);

  // ═══ CLOSE SUCCESS → NAVIGATE ═══
  const handleCloseSuccess = () => {
    setSuccessDialogOpen(false);
    navigate('/my-rentals');
  };

  // ═══ SUBMIT ORDER ═══
  const handleSubmit = async () => {
    if (!user) {
      setSnackbar({ open: true, message: isAr ? 'يرجى تسجيل الدخول أولاً' : 'Please login first', severity: 'warning' });
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
    if (!bookingForm.startDate) {
      setSnackbar({ open: true, message: isAr ? 'يرجى اختيار تاريخ البداية' : 'Please select start date', severity: 'warning' });
      return;
    }
    if (!bookingForm.duration || bookingForm.duration < (product.RentalMinDuration || 1)) {
      setSnackbar({ open: true, message: isAr ? `الحد الأدنى ${product.RentalMinDuration || 1}` : `Minimum is ${product.RentalMinDuration || 1}`, severity: 'warning' });
      return;
    }
    if (bookingForm.isDelivery && !selectedLocation) {
      setSnackbar({ open: true, message: isAr ? 'يرجى اختيار عنوان التوصيل' : 'Please select delivery location', severity: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ProductId: product.Id,
        CustomerId: user.Id || user.id,
        BranchId: branchId,
        RentalStartDate: bookingForm.startDate,
        RentalDuration: parseInt(bookingForm.duration),
        Currency: selectedCurrency,
        SelectedColor: bookingForm.selectedColor || null,
        CustomerNotes: bookingForm.customerNotes || null,
        IsDelivery: bookingForm.isDelivery,
        DeliveryAddress: bookingForm.isDelivery && selectedLocation
          ? `${selectedLocation.Name} - ${selectedLocation.Address}, ${selectedLocation.City}`
          : null,
        DeliveryLatitude: bookingForm.isDelivery && selectedLocation ? selectedLocation.Latitude : null,
        DeliveryLongitude: bookingForm.isDelivery && selectedLocation ? selectedLocation.Longitude : null,
      };
      const result = await createRentalOrder(payload);
      setCreatedOrder(result.RentalOrder || result);
      setSuccessDialogOpen(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.ErrorMessage || (isAr ? 'فشل إنشاء الطلب' : 'Failed to create order'),
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════
  // LOADING
  // ═══════════════════════════════════════════

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={44} width={140} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" height={isMobile ? 220 : 380} sx={{ borderRadius: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}><Skeleton variant="rounded" height={180} sx={{ borderRadius: 2 }} /></Grid>
            <Grid item xs={12} md={5}><Skeleton variant="rounded" height={350} sx={{ borderRadius: 2 }} /></Grid>
          </Grid>
        </Stack>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <HomeRepairServiceIcon sx={{ fontSize: 72, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" fontWeight={700}>{isAr ? 'المنتج غير موجود' : 'Product not found'}</Typography>
        <Button variant="contained" onClick={() => navigate('/rentals')} sx={{ mt: 2, borderRadius: 2 }}>
          {isAr ? 'العودة' : 'Go Back'}
        </Button>
      </Container>
    );
  }

  // ═══════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB', pb: { xs: 10, sm: 6 } }}>

      {/* Back */}
      <Box sx={{ maxWidth: 1700, mx: 'auto', pt: { xs: 1.5, sm: 2.5 }, px: { xs: 2, sm: 3, md: 5, lg: 7 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/rentals')}
          sx={{ mb: 1.5, fontWeight: 700, fontSize: '0.92rem', color: 'text.secondary', borderRadius: 2, px: 2 }}>
          {isAr ? 'العودة للمنتجات' : 'Back to Products'}
        </Button>
      </Box>

      <Box sx={{ maxWidth: 1700, mx: 'auto', px: { xs: 2, sm: 3, md: 5, lg: 7 } }}>
        <Grid container spacing={{ xs: 2.5, sm: 3.5, md: 4.5 }}>

          {/* ═══ LEFT: IMAGES + DESCRIPTION ═══ */}
          <Grid item xs={12} md={7} lg={7}>

            <Paper elevation={0} sx={{
              borderRadius: 2, overflow: 'hidden',
              border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1),
            }}>
              <Box sx={{
                position: 'relative', width: '100%',
                pt: { xs: '90%', sm: '72%', md: '68%' }, bgcolor: '#fafafa',
              }}>
                <Box component="img" src={productImages[selectedImageIndex] || '/placeholder.png'} alt={product.ProductName}
                  sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', p: { xs: 1.5, sm: 2.5 } }} />
                {productImages.length > 1 && (
                  <>
                    <IconButton onClick={() => setSelectedImageIndex(i => i > 0 ? i - 1 : productImages.length - 1)}
                      sx={{
                        position: 'absolute', left: { xs: 8, sm: 14 }, top: '50%', transform: 'translateY(-50%)',
                        bgcolor: alpha('#fff', 0.92), boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                        width: { xs: 40, sm: 46 }, height: { xs: 40, sm: 46 },
                        '&:hover': { bgcolor: '#fff' },
                      }}>
                      <ChevronLeftIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
                    </IconButton>
                    <IconButton onClick={() => setSelectedImageIndex(i => i < productImages.length - 1 ? i + 1 : 0)}
                      sx={{
                        position: 'absolute', right: { xs: 8, sm: 14 }, top: '50%', transform: 'translateY(-50%)',
                        bgcolor: alpha('#fff', 0.92), boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                        width: { xs: 40, sm: 46 }, height: { xs: 40, sm: 46 },
                        '&:hover': { bgcolor: '#fff' },
                      }}>
                      <ChevronRightIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
                    </IconButton>
                  </>
                )}
                {productImages.length > 1 && (
                  <Chip label={`${selectedImageIndex + 1}/${productImages.length}`} size="small"
                    sx={{ position: 'absolute', bottom: 12, right: 12, bgcolor: alpha('#000', 0.55), color: '#fff', fontWeight: 700, fontSize: '0.78rem', height: 28 }} />
                )}
              </Box>
              {productImages.length > 1 && (
                <Stack direction="row" spacing={1.2} sx={{ p: { xs: 1.5, sm: 2.5 }, overflowX: 'auto' }}>
                  {productImages.map((img, i) => (
                    <Box key={i} onClick={() => setSelectedImageIndex(i)}
                      sx={{
                        width: { xs: 62, sm: 76 }, height: { xs: 62, sm: 76 },
                        borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                        border: '2.5px solid', borderColor: i === selectedImageIndex ? 'primary.main' : 'transparent',
                        opacity: i === selectedImageIndex ? 1 : 0.5, transition: '0.2s',
                        '&:hover': { opacity: 0.8 },
                      }}>
                      <Box component="img" src={img} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>

            {/* Description */}
            <Paper elevation={0} sx={{
              mt: { xs: 2.5, sm: 3.5 }, p: { xs: 3, sm: 3.5, md: 4 }, borderRadius: 2,
              border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1),
            }}>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.35rem' } }}>
                {isAr ? 'الوصف' : 'Description'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 2, fontSize: { xs: '0.95rem', sm: '1rem', md: '1.08rem' } }}>
                {product.Description || (isAr ? 'لا يوجد وصف' : 'No description')}
              </Typography>

              {product.RentalTerms && (
                <Box sx={{
                  mt: 3.5, p: { xs: 2.5, sm: 3 },
                  bgcolor: alpha(theme.palette.warning.main, 0.04),
                  borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.15),
                }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <DescriptionIcon sx={{ color: 'warning.main', fontSize: 22 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                      {isAr ? 'شروط التأجير' : 'Rental Terms'}
                    </Typography>
                  </Stack>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9, whiteSpace: 'pre-line', fontSize: { xs: '0.92rem', sm: '0.98rem' } }}>
                    {product.RentalTerms}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ═══ RIGHT: BOOKING PANEL ═══ */}
          <Grid item xs={12} md={5} lg={5}>
            <Paper elevation={0} sx={{
              p: { xs: 3, sm: 3.5, md: 4 }, borderRadius: 2,
              border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1),
              position: { md: 'sticky' }, top: { md: 80 },
              animation: `${fadeInUp} 0.4s ease-out`,
            }}>

              {/* Title */}
              <Typography variant="h4" fontWeight={900} sx={{ fontSize: { xs: '1.3rem', sm: '1.45rem', md: '1.65rem' }, mb: 0.8, lineHeight: 1.3 }}>
                {product.ProductName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 0.6 }}>
                <Chip label={isAr ? 'للإيجار' : 'For Rent'} color="primary" size="small" sx={{ fontWeight: 700, fontSize: '0.78rem', height: 30 }} />
                {product.RentalStock != null && (
                  <Chip icon={<InventoryIcon sx={{ fontSize: 15 }} />} label={`${product.RentalStock} ${isAr ? 'متاح' : 'left'}`} variant="outlined" size="small" sx={{ fontWeight: 600, fontSize: '0.78rem', height: 30 }} />
                )}
              </Stack>

              {/* Price */}
              <Box sx={{
                p: { xs: 2.5, sm: 3 }, borderRadius: 2, mb: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.1),
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.75rem', mb: 0.3 }}>
                      {isAr ? 'سعر الإيجار' : 'Rental Price'}
                    </Typography>
                    <Typography variant="h3" color="primary.main" fontWeight={900} sx={{ fontSize: { xs: '1.85rem', sm: '2.2rem', md: '2.6rem' }, lineHeight: 1.1 }}>
                      {rentalPrice.toLocaleString()}
                      <Typography component="span" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' }, fontWeight: 600, ml: 0.4 }}>{currencySymbol}</Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem', mt: 0.3 }}>
                      / {isAr ? priceConfig.labelAr : priceConfig.labelEn}
                    </Typography>
                  </Box>
                  {depositPrice > 0 && (
                    <Tooltip title={isAr ? 'تأمين مسترد' : 'Refundable deposit'} arrow>
                      <Stack alignItems="center" spacing={0.4}>
                        <SecurityIcon sx={{ color: 'warning.main', fontSize: 24 }} />
                        <Typography variant="body2" fontWeight={700} color="warning.main" sx={{ fontSize: '0.88rem' }}>
                          {depositPrice.toLocaleString()} {currencySymbol}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {isAr ? 'تأمين' : 'Deposit'}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  )}
                </Stack>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Colors */}
              {productColors && productColors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 1.5 }}>
                    <PaletteIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>{isAr ? 'اللون' : 'Color'}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {productColors.map((color, i) => (
                      <Chip key={i} label={color.colorName}
                        avatar={color.colorImage ? <Avatar src={getImageUrl(color.colorImage)} sx={{ width: 26, height: 26 }} /> : undefined}
                        onClick={() => setBookingForm(p => ({ ...p, selectedColor: color.colorName }))}
                        variant={bookingForm.selectedColor === color.colorName ? 'filled' : 'outlined'}
                        color={bookingForm.selectedColor === color.colorName ? 'primary' : 'default'}
                        sx={{ fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', height: 38 }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Form */}
              <Stack spacing={2.5}>
                {/* Date Field - Clickable anywhere, Arabic placeholder */}
                <Box
                  sx={{ position: 'relative', width: '100%' }}
                  onClick={() => {
                    // Programmatically open the hidden native date picker
                    const dateInput = document.getElementById('rental-start-date-input');
                    if (dateInput) {
                      try {
                        dateInput.showPicker(); // Modern browsers (Chrome, Edge, Safari 16+)
                      } catch {
                        dateInput.focus(); // Fallback for older browsers
                        dateInput.click();
                      }
                    }
                  }}
                >
                  {/* Visible styled TextField (display only) */}
                  <TextField
                    fullWidth
                    label={isAr ? 'تاريخ البداية' : 'Start Date'}
                    value={
                      bookingForm.startDate
                        ? isAr
                          ? new Date(bookingForm.startDate).toLocaleDateString('ar-EG', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })
                          : new Date(bookingForm.startDate).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })
                        : ''
                    }
                    placeholder={isAr ? 'اليوم/الشهر/السنة' : 'dd/mm/yyyy'}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: true,
                      sx: { pointerEvents: 'none' }, // ✅ Let clicks pass through to parent Box
                      endAdornment: (
                        <CalendarMonthIcon
                          sx={{
                            fontSize: 22,
                            color: 'action.active',
                            pointerEvents: 'none',
                          }}
                        />
                      ),
                    }}
                    sx={{
                      ...inputSx,
                      '& .MuiInputBase-root': {
                        cursor: 'pointer',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        fontSize: { xs: '0.9rem', sm: '0.95rem' },
                      },
                      '& .MuiInputBase-input': {
                        cursor: 'pointer',
                        '&::placeholder': {
                          fontSize: isAr ? '0.95rem' : '0.875rem',
                          direction: isAr ? 'rtl' : 'ltr',
                          opacity: 0.55,
                        },
                      },
                    }}
                  />

                  {/* Hidden native date input — positioned off-screen but still functional */}
                  <input
                    id="rental-start-date-input"
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={bookingForm.startDate || ''}
                    onChange={(e) =>
                      setBookingForm((p) => ({ ...p, startDate: e.target.value }))
                    }
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '1px',
                      height: '1px',
                      opacity: 0,
                      pointerEvents: 'none', // ✅ Don't block clicks — we trigger it programmatically
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      overflow: 'hidden',
                    }}
                  />
                </Box>

                <TextField fullWidth type="number"
                  label={isAr ? `المدة (${priceConfig.pluralAr})` : `Duration (${priceConfig.pluralEn})`}
                  value={bookingForm.duration}
                  onChange={e => {
                    const val = Math.max(product.RentalMinDuration || 1, parseInt(e.target.value) || 1);
                    setBookingForm(p => ({ ...p, duration: product.RentalMaxDuration ? Math.min(val, product.RentalMaxDuration) : val }));
                  }}
                  inputProps={{ min: product.RentalMinDuration || 1, max: product.RentalMaxDuration || 999 }}
                  helperText={`${isAr ? 'المدة:' : 'Range:'} ${product.RentalMinDuration || 1} - ${product.RentalMaxDuration || '∞'} ${isAr ? priceConfig.pluralAr : priceConfig.pluralEn}`}
                  sx={inputSx} />

                {bookingForm.endDate && (
                  <Alert severity="info" icon={<CalendarMonthIcon sx={{ fontSize: 20 }} />}
                    sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.92rem' } }}>
                    {isAr ? 'تاريخ الانتهاء:' : 'End date:'}{' '}
                    <strong>
                      {new Date(bookingForm.endDate).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', {
                        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                      })}
                    </strong>
                  </Alert>
                )}

                {/* Receive Method */}
                <Box sx={{
                  p: 2.5, borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1),
                }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, fontSize: '0.95rem' }}>
                    {isAr ? 'طريقة الاستلام' : 'Receive Method'}
                  </Typography>

                  <ToggleButtonGroup
                    value={bookingForm.isDelivery ? 'delivery' : 'pickup'} exclusive
                    onChange={(_, val) => { if (val !== null) setBookingForm(p => ({ ...p, isDelivery: val === 'delivery' })); }}
                    fullWidth
                    sx={{
                      '& .MuiToggleButton-root': {
                        borderRadius: '10px !important', border: '1.5px solid',
                        borderColor: alpha(theme.palette.divider, 0.15),
                        textTransform: 'none', fontWeight: 600, fontSize: '0.95rem', py: 1.8,
                        '&.Mui-selected': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          borderColor: theme.palette.primary.main, color: theme.palette.primary.main,
                        },
                      }, gap: 1.2,
                    }}
                  >
                    <ToggleButton value="pickup" sx={{ flex: 1 }}>
                      <Stack alignItems="center" spacing={0.6}>
                        <StorefrontIcon sx={{ fontSize: 28 }} />
                        <span>{isAr ? 'من المتجر' : 'In-Shop'}</span>
                      </Stack>
                    </ToggleButton>
                    <ToggleButton value="delivery" sx={{ flex: 1 }}>
                      <Stack alignItems="center" spacing={0.6}>
                        <LocalShippingIcon sx={{ fontSize: 28 }} />
                        <span>{isAr ? 'توصيل' : 'Delivery'}</span>
                      </Stack>
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Collapse in={bookingForm.isDelivery} timeout={250}>
                    <Stack spacing={1.8} sx={{ mt: 2.5 }}>
                      {loadingLocations ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.92rem' }}>
                            {isAr ? 'جاري التحميل...' : 'Loading...'}
                          </Typography>
                        </Stack>
                      ) : userLocations.length > 0 ? (
                        <>
                          <FormControl fullWidth>
                            <InputLabel sx={{ fontSize: '0.95rem' }}>{isAr ? 'عنوان التوصيل' : 'Delivery Location'}</InputLabel>
                            <Select
                              value={selectedLocationId || ''} label={isAr ? 'عنوان التوصيل' : 'Delivery Location'}
                              onChange={(e) => setSelectedLocationId(e.target.value)}
                              sx={{ borderRadius: 2, bgcolor: 'background.paper', fontSize: '0.95rem' }}
                              MenuProps={{ disablePortal: false, sx: { zIndex: 99999 }, PaperProps: { sx: { borderRadius: 2, mt: 0.5, maxHeight: 320 } } }}
                            >
                              {userLocations.map((loc) => {
                                const LocIcon = getLocationIcon(loc.Name);
                                return (
                                  <MenuItem key={loc.Id} value={loc.Id} sx={{ py: 1.5, px: 2.5 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                                      <Box sx={{
                                        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}>
                                        <LocIcon sx={{ fontSize: 19, color: 'primary.main' }} />
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={0.6} alignItems="center">
                                          <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>{loc.Name}</Typography>
                                          {loc.IsDefault && (
                                            <Chip label={isAr ? 'افتراضي' : 'Default'} size="small" color="primary" variant="outlined"
                                              sx={{ fontSize: '0.6rem', height: 20, '& .MuiChip-label': { px: 0.5 } }} />
                                          )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.82rem' }}>
                                          {loc.Address}{loc.City ? `, ${loc.City}` : ''}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>

                          {selectedLocation && (
                            <Paper elevation={0} sx={{
                              p: 2, borderRadius: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.03),
                              border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.08),
                            }}>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box sx={{
                                  width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <LocationOnIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>{selectedLocation.Name}</Typography>
                                  <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.82rem' }}>
                                    {selectedLocation.Address}{selectedLocation.City ? `, ${selectedLocation.City}` : ''}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          )}

                          {distance > 0 && (
                            <Alert severity="info" icon={<LocalShippingIcon sx={{ fontSize: 19 }} />}
                              sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.88rem' } }}>
                              {isAr ? 'المسافة:' : 'Distance:'} <strong>{distance.toFixed(1)} km</strong>
                              {' · '}{isAr ? 'التوصيل:' : 'Fee:'} <strong>{deliveryFee.toLocaleString()} {currencySymbol}</strong>
                            </Alert>
                          )}
                        </>
                      ) : (
                        <Paper elevation={0} sx={{
                          p: 3, borderRadius: 2, textAlign: 'center',
                          bgcolor: alpha(theme.palette.warning.main, 0.04),
                          border: '1px dashed', borderColor: alpha(theme.palette.warning.main, 0.25),
                        }}>
                          <AddLocationIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1rem', mb: 0.5 }}>
                            {isAr ? 'لا توجد عناوين' : 'No saved locations'}
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: '0.9rem' }}>
                            {isAr ? 'أضف عنوان لإكمال الطلب' : 'Add address to complete order'}
                          </Typography>
                          <Button variant="outlined" color="warning" startIcon={<AddLocationIcon />}
                            onClick={() => navigate('/profile/locations')}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.92rem', py: 1 }}>
                            {isAr ? 'إضافة عنوان' : 'Add Location'}
                          </Button>
                        </Paper>
                      )}
                    </Stack>
                  </Collapse>
                </Box>

                {/* Notes */}
                <TextField fullWidth multiline rows={3}
                  label={isAr ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
                  value={bookingForm.customerNotes}
                  onChange={e => setBookingForm(p => ({ ...p, customerNotes: e.target.value }))}
                  placeholder={isAr ? 'أضف ملاحظات...' : 'Add notes...'}
                  sx={inputSx} />
              </Stack>

              {/* Price Summary */}
              <Paper elevation={0} sx={{
                mt: 3, p: 2.5, borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                border: '1px solid', borderColor: alpha(theme.palette.divider, 0.08),
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, fontSize: '1.02rem' }}>
                  {isAr ? 'ملخص الأسعار' : 'Price Summary'}
                </Typography>
                <Stack spacing={1.2}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                      {rentalPrice.toLocaleString()} × {bookingForm.duration} {isAr ? priceConfig.pluralAr : priceConfig.pluralEn}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1rem' }}>
                      {totalRentalPrice.toLocaleString()} {currencySymbol}
                    </Typography>
                  </Stack>

                  {depositPrice > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>{isAr ? 'التأمين' : 'Deposit'}</Typography>
                        <Chip label={isAr ? 'مسترد' : 'Refundable'} size="small" color="success" variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 20, '& .MuiChip-label': { px: 0.5 } }} />
                      </Stack>
                      <Typography variant="body1" fontWeight={600} color="warning.main" sx={{ fontSize: '1rem' }}>
                        {depositPrice.toLocaleString()} {currencySymbol}
                      </Typography>
                    </Stack>
                  )}

                  {bookingForm.isDelivery && (
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <LocalShippingIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>{isAr ? 'التوصيل' : 'Delivery'}</Typography>
                        {distance > 0 && (
                          <Chip label={`${distance.toFixed(1)}km`} size="small" variant="outlined"
                            sx={{ fontSize: '0.6rem', height: 20, '& .MuiChip-label': { px: 0.4 } }} />
                        )}
                      </Stack>
                      <Typography variant="body1" fontWeight={600} color="info.main" sx={{ fontSize: '1rem' }}>
                        {deliveryFee > 0 ? `${deliveryFee.toLocaleString()} ${currencySymbol}` : (isAr ? 'مجاني' : 'Free')}
                      </Typography>
                    </Stack>
                  )}

                  <Divider sx={{ my: 0.8 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.15rem' }}>
                      {isAr ? 'الإجمالي' : 'Total'}
                    </Typography>
                    <Typography variant="h3" fontWeight={900} color="primary.main" sx={{ fontSize: { xs: '1.6rem', sm: '1.85rem', md: '2.1rem' } }}>
                      {grandTotal.toLocaleString()} {currencySymbol}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              {/* Submit */}
              <Button fullWidth variant="contained" size="large" onClick={handleSubmit}
                disabled={!isAvailable || submitting || !bookingForm.startDate || (bookingForm.isDelivery && !selectedLocation)}
                startIcon={submitting ? <CircularProgress size={22} color="inherit" /> : <ShoppingCartCheckoutIcon />}
                sx={{
                  mt: 3, py: 2, borderRadius: 2,
                  fontWeight: 800, fontSize: { xs: '1.02rem', sm: '1.12rem', md: '1.18rem' },
                  textTransform: 'none',
                  background: isAvailable ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` : undefined,
                  boxShadow: isAvailable ? `0 6px 24px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 10px 32px ${alpha(theme.palette.primary.main, 0.4)}` },
                  '&:active': { transform: 'translateY(0)' },
                }}>
                {submitting ? (isAr ? 'جاري الحجز...' : 'Booking...')
                  : !isAvailable ? (isAr ? 'غير متاح' : 'Unavailable')
                  : (isAr ? 'تأكيد الحجز' : 'Confirm Booking')}
              </Button>

              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2, flexWrap: 'wrap', gap: 0.6 }}>
                <Chip icon={<VerifiedIcon sx={{ fontSize: 15 }} />} label={isAr ? 'حجز فوري' : 'Instant'} size="small" variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 28, '& .MuiChip-label': { px: 0.8 } }} />
                <Chip icon={<SecurityIcon sx={{ fontSize: 15 }} />} label={isAr ? 'آمن' : 'Secure'} size="small" variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 28, '& .MuiChip-label': { px: 0.8 } }} />
                {bookingForm.isDelivery && (
                  <Chip icon={<LocalShippingIcon sx={{ fontSize: 15 }} />} label={isAr ? 'توصيل' : 'Delivery'} size="small" color="primary" variant="outlined"
                    sx={{ fontSize: '0.75rem', height: 28, '& .MuiChip-label': { px: 0.8 } }} />
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      
      {/* ═══════════════════════════════════════════
          SUCCESS DIALOG — PORTAL TO BODY, ABOVE EVERYTHING
          ═══════════════════════════════════════════ */}
      <Portal>
        <Dialog
          open={successDialogOpen}
          onClose={handleCloseSuccess}
          maxWidth="xs"
          fullWidth
          disableScrollLock={false}
          slotProps={{
            backdrop: {
              sx: {
                zIndex: 99998,
                backgroundColor: 'rgba(0,0,0,0.6)',
              },
            },
          }}
          sx={{
            zIndex: 99999,
            position: 'fixed',
            '& .MuiDialog-container': {
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: 'hidden',
              m: 2,
              maxHeight: '88vh',
              position: 'relative',
              zIndex: 99999,
            },
          }}
        >
          {/* Green header */}
          <Box sx={{
            textAlign: 'center',
            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
            py: { xs: 3.5, sm: 4.5 }, px: 2, position: 'relative',
          }}>
            <IconButton
              onClick={handleCloseSuccess}
              sx={{
                position: 'absolute', top: 6, right: 6,
                color: alpha('#fff', 0.8), bgcolor: alpha('#fff', 0.15),
                width: 32, height: 32,
                '&:hover': { bgcolor: alpha('#fff', 0.3), color: '#fff' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box sx={{
              width: 60, height: 60, borderRadius: '50%', bgcolor: alpha('#fff', 0.2),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 1.5, animation: `${pulse} 1.5s ease-in-out infinite`,
            }}>
              <CheckCircleIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '1.15rem', sm: '1.3rem' }, mb: 0.3 }}>
              {isAr ? 'تم الحجز بنجاح! 🎉' : 'Booking Confirmed! 🎉'}
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#fff', 0.85), fontSize: '0.82rem' }}>
              {isAr ? 'تم إنشاء طلب التأجير' : 'Your rental order has been created'}
            </Typography>
          </Box>

          <DialogContent sx={{ py: 2.5, px: 2.5 }}>
            <Stack spacing={1.5}>

              {/* Order ID */}
              {createdOrder?.Id && (
                <Paper elevation={0} sx={{
                  p: 1.2, textAlign: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 2,
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                    {isAr ? 'رقم الطلب' : 'Order ID'}
                  </Typography>
                  <Typography variant="h5" fontWeight={900} color="primary.main">#{createdOrder.Id}</Typography>
                </Paper>
              )}

              {/* Details */}
              <Stack spacing={0.6} sx={{
                p: 1.5, borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                border: '1px solid', borderColor: alpha(theme.palette.divider, 0.08),
              }}>
                {[
                  { label: isAr ? 'المنتج' : 'Product', value: product?.ProductName },
                  { label: isAr ? 'المدة' : 'Duration', value: `${bookingForm.duration} ${isAr ? priceConfig.pluralAr : priceConfig.pluralEn}` },
                  { label: isAr ? 'البداية' : 'Start', value: bookingForm.startDate ? new Date(bookingForm.startDate).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric' }) : '-' },
                  { label: isAr ? 'النهاية' : 'End', value: bookingForm.endDate ? new Date(bookingForm.endDate).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric' }) : '-' },
                  { label: isAr ? 'الاستلام' : 'Method', value: bookingForm.isDelivery ? (isAr ? '🚚 توصيل' : '🚚 Delivery') : (isAr ? '🏪 من المتجر' : '🏪 In-Shop') },
                  ...(bookingForm.isDelivery && selectedLocation ? [{ label: isAr ? 'العنوان' : 'Address', value: selectedLocation.Name }] : []),
                ].map((row, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" sx={{ py: 0.2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>{row.label}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem', maxWidth: '55%', textAlign: 'right' }} noWrap>{row.value}</Typography>
                  </Stack>
                ))}

                <Divider sx={{ my: 0.3 }} />

                {/* Prices */}
                <Stack direction="row" justifyContent="space-between" sx={{ py: 0.2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>{isAr ? 'الإيجار' : 'Rental'}</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem' }}>
                    {(createdOrder?.RentalTotalPrice || totalRentalPrice).toLocaleString()} {currencySymbol}
                  </Typography>
                </Stack>

                {(createdOrder?.DepositAmount || depositPrice) > 0 && (
                  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>{isAr ? 'التأمين' : 'Deposit'}</Typography>
                    <Typography variant="body2" fontWeight={600} color="warning.main" sx={{ fontSize: '0.78rem' }}>
                      {(createdOrder?.DepositAmount || depositPrice).toLocaleString()} {currencySymbol}
                    </Typography>
                  </Stack>
                )}

                {(createdOrder?.IsDelivery || bookingForm.isDelivery) && (
                  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>{isAr ? 'التوصيل' : 'Delivery'}</Typography>
                    <Typography variant="body2" fontWeight={600} color="info.main" sx={{ fontSize: '0.78rem' }}>
                      {(createdOrder?.DeliveryFee || deliveryFee || 0) > 0
                        ? `${Number(createdOrder?.DeliveryFee || deliveryFee).toLocaleString()} ${currencySymbol}`
                        : (isAr ? 'مجاني' : 'Free')}
                    </Typography>
                  </Stack>
                )}

                {createdOrder?.Distance > 0 && (
                  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>{isAr ? 'المسافة' : 'Distance'}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem' }}>{createdOrder.Distance} km</Typography>
                  </Stack>
                )}

                <Divider sx={{ my: 0.3 }} />

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: '0.85rem' }}>{isAr ? 'الإجمالي' : 'Total'}</Typography>
                  <Typography variant="subtitle1" fontWeight={900} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                    {(createdOrder?.GrandTotal || grandTotal).toLocaleString()} {currencySymbol}
                  </Typography>
                </Stack>
              </Stack>

              {bookingForm.selectedColor && (
                <Stack direction="row" justifyContent="center">
                  <Chip icon={<PaletteIcon sx={{ fontSize: 12 }} />} label={bookingForm.selectedColor} color="primary" variant="outlined" size="small"
                    sx={{ fontSize: '0.7rem', height: 24 }} />
                </Stack>
              )}

              <Alert severity="info" sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                {bookingForm.isDelivery
                  ? (isAr ? 'سيتم تعيين سائق لتوصيل المنتج. تابع من صفحة طلباتي.' : 'A driver will deliver your product. Track from My Rentals.')
                  : (isAr ? 'استلم من المتجر. سنتواصل معك للتأكيد.' : 'Pick up from store. We will contact you to confirm.')}
              </Alert>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button variant="outlined" fullWidth
              onClick={() => { setSuccessDialogOpen(false); navigate('/rentals'); }}
              sx={{ borderRadius: 2, fontWeight: 700, py: 1, fontSize: '0.82rem', order: { xs: 2, sm: 1 } }}>
              {isAr ? 'تصفح المزيد' : 'Browse More'}
            </Button>
            <Button variant="contained" fullWidth
              onClick={handleCloseSuccess}
              sx={{ borderRadius: 2, fontWeight: 700, py: 1, fontSize: '0.82rem', order: { xs: 1, sm: 2 } }}>
              {isAr ? 'طلباتي' : 'My Rentals'}
            </Button>
          </DialogActions>
        </Dialog>
      </Portal>

      {/* ═══════════════════════════════════════════
          SNACKBAR — ALSO IN PORTAL
          ═══════════════════════════════════════════ */}
      <Portal>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ zIndex: 100000 }}
        >
          <Alert
            onClose={() => setSnackbar(p => ({ ...p, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%', borderRadius: 2, fontWeight: 600, fontSize: '0.82rem' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Portal>
    </Box>
  );
}